/**
 * Phase 4 — bulk pre-trial notice upload (Lloyd, Jul 22: "Please prioritize it, so that we can
 * switch over"). Two calls with a human review in between: extract reads the scans and proposes,
 * commit writes only what was approved.
 */
import type { AppearanceRequirement } from './CourtEventApi';


export interface ProposedEventDto {
  title: string;
  /** ISO YYYY-MM-DD as printed on the notice. */
  date: string;
  /** 24h HH:MM. */
  time: string;
  startEpoch: number;
  endEpoch: number;
  court?: string;
  description?: string;
  /** The notice gave no time and 09:00 was assumed — show it, do not hide it. */
  timeAssumed: boolean;
  duplicateOf?: { kind: 'exact' | 'same-day'; existingStartEpoch: number };
  /** Whether the row opens ticked. Duplicates never do. */
  preselected: boolean;
  /**
   * What the notice implied about attendance, if anything — set only when it said so in words.
   * Undefined is the common case and means the reviewer starts on the safe default.
   */
  suggestedAppearance?: AppearanceRequirement;
  /** The notice's own words, so the reviewer can check the suggestion against the document. */
  appearanceQuote?: string;
  /** The reviewer's answer, sent back on commit. Theirs overrules the suggestion. */
  appearanceRequirement?: AppearanceRequirement;
}

export type NoticeKind = 'pre-trial' | 'show-cause' | 'bond' | 'offer' | 'court-order' | 'other';

export interface BulkNoticeReviewRow {
  token: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  confidence: 'high' | 'medium' | 'low';
  citationNumber?: string;
  /** The court's own cause / docket number. What most Texas notices are actually identified by. */
  causeNumber?: string;
  defendantName?: string;
  /** What the document is — 'show-cause' and 'bond' need a person, not just a calendar entry. */
  noticeKind?: NoticeKind;
  /** Present when this file needs acting on rather than simply filing. */
  urgentNote?: string;
  match: {
    ticketGuid: string;
    ticketNumber: string;
    clientName: string;
    matchedBy: 'citation' | 'cause' | 'name';
    strength: 'strong' | 'weak';
  } | null;
  matchNote: string;
  events: ProposedEventDto[];
  rejected: string[];
  error?: string;
}

export interface BulkNoticeBatch {
  batchId: string;
  rows: BulkNoticeReviewRow[];
  summary: {
    files: number;
    matched: number;
    unmatched: number;
    proposedEvents: number;
    duplicates: number;
    failed: number;
  };
}

export interface ApprovedNotice {
  token: string;
  fileName: string;
  ticketGuid: string;
  /**
   * Echoed back so the server can record it on the case the reviewer chose.
   *
   * This is what stops the matcher failing the same way forever: our cause number is usually blank,
   * so the first notice from a court needs a person — and recording what they placed it against
   * means the next one matches itself.
   */
  causeNumber?: string;
  /** Echoed back so filing a court order alerts staff. The server keeps no copy of the reading. */
  noticeKind?: NoticeKind;
  events: Array<{
    title: string;
    date: string;
    time?: string;
    court?: string;
    description?: string;
    /** Must be sent — the extraction-time suggestion is not retained server-side. */
    appearanceRequirement?: AppearanceRequirement;
  }>;
  attachDocument?: boolean;
  /**
   * The reviewer's decision that this is a bond and should go to the court. Never derived from
   * `noticeKind` — the parser's guess is a prompt, a person ticks the box (Lloyd, Aug 1).
   */
  sendBondToCourt?: boolean;
}

export type BondDeliveryOutcome = 'sent-to-court' | 'draft-for-review' | 'routed-internal';

export interface CommitNoticeResult {
  fileName: string;
  ticketNumber?: string;
  eventsCreated: number;
  duplicatesSkipped: number;
  documentAttached: boolean;
  /** Present only when a bond was marked. `note` says plainly whether the court actually has it. */
  bondDelivery?: {
    outcome: BondDeliveryOutcome;
    recipients: string[];
    note: string;
  };
  errors: string[];
}

export interface CommitBulkNoticesResult {
  results: CommitNoticeResult[];
  totals: {
    eventsCreated: number;
    duplicatesSkipped: number;
    documentsAttached: number;
    bondsDelivered: number;
    failed: number;
  };
}

/**
 * What the file picker offers, mirroring the server's `noticeFileTypes.ts`.
 *
 * The two had drifted: the parser reads TIFF, BMP, WEBP and .mhtml, and the picker listed none of
 * them. A single-file "Save as" from Outlook or Chrome produces .mhtml, so the saved emails staff
 * were told to upload were filtered out of the dialog before the server ever saw them.
 *
 * `testNoticeFileTypeParity` checks both directions against the server's own classifier.
 */
export const ACCEPTED_NOTICE_TYPES =
  'application/pdf,image/jpeg,image/png,image/tiff,image/bmp,image/webp,text/html,' +
  'multipart/related,.pdf,.jpg,.jpeg,.png,.tif,.tiff,.bmp,.webp,.html,.htm,.mhtml';

class BulkNoticeApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  /** Read a pile of scans. Creates nothing — everything comes back for review. */
  static async extract(files: File[]): Promise<BulkNoticeBatch> {
    const form = new FormData();
    files.forEach((file) => form.append('notices', file));

    const response = await fetch(
      `${BulkNoticeApi.host()}/api/v1/manager/court-notices/bulk-extract`,
      { method: 'POST', credentials: 'include', body: form },
    );
    if (!response.ok) {
      // A gateway status means the proxy gave up while the server was still reading — the files
      // were fine and the upload succeeded. Saying "could not read the uploaded notices" sent staff
      // hunting for a bad scan that did not exist (Ashley, 22 files, Jul 29). These responses are
      // HTML error pages, so response.json() fails and there is no server message to show.
      if (response.status === 504 || response.status === 502 || response.status === 408) {
        throw new Error(
          'The notices were uploaded, but reading them took longer than the server allows. ' +
            'Nothing was saved. Please try again with fewer files at a time.',
        );
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Could not read the uploaded notices.');
    }
    return response.json();
  }

  /** Create the court dates a reviewer approved and file each scan under its case. */
  static async commit(
    batchId: string,
    approvals: ApprovedNotice[],
  ): Promise<CommitBulkNoticesResult> {
    const response = await fetch(
      `${BulkNoticeApi.host()}/api/v1/manager/court-notices/bulk-commit`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId, approvals }),
      },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Could not save the approved notices.');
    }
    return response.json();
  }
}

export default BulkNoticeApi;
